import { useState } from "react";
import videojs from "video.js/core.es.js";
import config from "@/modules/config";
import * as playbackResume from "@/modules/playbackResume";
import * as playerRegistry from "@/modules/playerRegistry";
import type { LandscapeRecomposite } from "@/components/player/useLandscapeRecomposite";
import type { PlayerEvents } from "@/components/player/usePlayerEvents";
import type { PlayerSource } from "@/components/player/usePlayerSource";
import type { VideoFullscreen } from "@/components/player/useVideoFullscreen";
import type { VideoTracking } from "@/components/player/useVideoTracking";
import {
  type FullscreenDocument,
  templateSizes,
  type VideoJsSelf,
  type VideoPlayer,
} from "@/components/player/videoJsTypes";

// Creates or adopts the player on mount, re-applies on update, parks on unmount.
function createPlayerLifecycle(
  self: VideoJsSelf,
  tracking: VideoTracking,
  fullscreen: VideoFullscreen,
  source: PlayerSource,
  events: PlayerEvents,
  recomposite: LandscapeRecomposite,
) {
  function mount() {
    const experienceData = self.props.experienceState.experienceData;
    const id = experienceData ? experienceData.idexperience : undefined;

    self.articleTime = -1;
    self.percentComplete = 0;
    self.isSeeking = false;
    self.videoEndedFired = false;
    self.videoEndedPending = false;

    // A rotate remounts <VideoJs> for the SAME experience. Adopt the live player
    // instead of creating a new one — the same element already earned playback
    // permission, so sound + position + mute survive with no resume play() and
    // no poster re-flash / re-size.
    const persisted = playerRegistry.acquire(id) as VideoPlayer | null;
    if (persisted) {
      self.player = persisted;
      // Re-host the live `.video-js` node into the freshly-rendered layout host.
      playerRegistry.adopt(self.player, self.videoHost);
      self.videoElement =
        self.player.el().querySelector("video.vjs-tech") ||
        self.player.el().querySelector("video");
      // The persistent player kept the fixed pixel width/height it was created
      // with. A fresh create always re-measured for the new orientation; the
      // adopt path must do the same, or after a landscape->portrait rotate the
      // portrait box renders at the stale (landscape) size — leaving the
      // right-side gap. See syncPlayerDimensions for why this ALSO runs on every
      // update.
      source.syncPlayerDimensions();
      // Seed the id/loaded guards so updatePlayer treats this mount as a no-op:
      // it must NOT re-set the src, re-show the poster, or reset watch state.
      self.idexperience = id;
      self.appliedExperienceLoaded = experienceData.isLoaded();
      self.mediaReadyExperienceId = id;
      self.timelineName = self.props.timelineName;
      // Re-attach this mount's listeners + control-bar decorations to the
      // persistent player (the previous mount detached its own on unmount),
      // and re-run the fullscreen/pseudo-fs reconciliation for the new host.
      events.attachToPlayer(self.player, true);
      source.updatePlayer();
      // Re-measure the video content rect for the overlays in the new layout box.
      events.updateVideoContentRect();
      // After a rotate TO landscape, iOS Safari can leave the native control bar
      // painted behind the video's layer (play/pause covered) until some event
      // forces a recomposite — a screenshot fixes it live. Nudge the promoted bar
      // across the rotate-settle window to force that composite.
      recomposite.recompositeLandscapeControls();
      return;
    }

    // First load, or a chain advance to a new experience: create a fresh player.
    playerRegistry.disposeStale(id);
    self.idexperience = -1;

    const sizes = templateSizes(self.props.experienceState).player.video;

    // Whether this document is actually permitted to enter fullscreen. It is
    // false when a Permissions-Policy denies fullscreen — e.g. the MCP app card,
    // where the player runs in a cross-origin iframe nested inside a sandbox that
    // never delegates the `fullscreen` feature down the chain. In that context the
    // fullscreen control is inert (Chrome shows a dead button) and worse: reading
    // the fullscreen API while building the control bar throws in WebKit, aborting
    // the whole mount. When fullscreen is unavailable we omit the control entirely.
    const fsAvailable = !!(
      document.fullscreenEnabled ||
      (document as FullscreenDocument).webkitFullscreenEnabled
    );

    const playerOptions: Record<string, unknown> = {
      width: sizes.width,
      height: sizes.height,
      controls: true,
      // "metadata" (not "auto"): fetch headers up front but let video
      // bytes load as the user watches. The warm pipeline has already
      // primed the HTTP cache for intended plays, so start stays fast
      // while un-watched opens no longer pull the whole file.
      preload: "metadata",
      techOrder: ["Html5"],
      html5: {},
      // video.js's default double-click toggles fullscreen. Substitute our own so
      // we can attribute the fullscreen enter to the double-click in analytics —
      // video.js still applies its control-bar/modal guard before calling this,
      // and we replicate the exact default toggle. The event is emitted on the
      // actual enter (trackFullscreenEnter). Where fullscreen is unavailable,
      // disable double-click-to-fullscreen rather than fire a requestFullscreen
      // that can only reject.
      userActions: {
        doubleClick: fsAvailable
          ? () => fullscreen.onDoubleClickFullscreen()
          : false,
      },
      plugins: {
        timelineMarkers: {
          onMarkerMouseDown: tracking.onMarkerMouseDown,
          onMarkerReached: tracking.onMarkerReached,
        },
        videoJsResolutionSwitcher: {
          dynamicLabel: false,
        },
      },
    };

    // Drop the fullscreen toggle when fullscreen can't work here. Building it
    // reads the fullscreen API, which throws in WebKit under a denied
    // Permissions-Policy; and the button would be inert anyway.
    if (!fsAvailable) {
      playerOptions.controlBar = { fullscreenToggle: false };
    }

    // Create the <video> imperatively into the (React-owned but always empty)
    // host wrapper. Keeping the live `.video-js` node out of React's child tree
    // is what lets the registry relocate it across a rotate without React's DOM
    // reconciliation fighting the move.
    const videoEl = document.createElement("video");
    videoEl.className =
      "video-js vjs-16-9 vjs-default-skin vjs-big-play-centered";
    videoEl.setAttribute("playsinline", "");
    (self.videoHost as HTMLDivElement).appendChild(videoEl);
    self.videoElement = videoEl;

    self.player = videojs(videoEl, playerOptions, () => {
      /* no-op */
    }) as VideoPlayer;
    playerRegistry.register(id, self.player);

    // Attach this mount's listeners now and its control-bar decorations once
    // the player is ready. The same path re-runs on the adopt branch above.
    events.attachToPlayer(self.player, false);

    // One-time init that only makes sense on a freshly created player: the
    // deep-link ?t= seek and the cross-remount resume net. On the adopt path the
    // live player already holds the right position/mute, so neither runs there —
    // which is exactly why sound survives the rotate (no force-mute resume).
    self.player.ready(() => {
      if (!self.player || self.player.isDisposed()) {
        return;
      }
      // SeekToAction support — seek to ?t=<seconds> on initial load.
      // Enables Google key moments deep-linking (schema.org SeekToAction)
      // and OpenAI Ads play-url deep-links.
      try {
        const seekSeconds = config.getSeekToSeconds();
        // Seek only once; guard against re-seeking on quality/resolution switches.
        if ((seekSeconds as number) > 0 && !self.didSeekToParam) {
          self.didSeekToParam = true;
          const player = self.player;
          const applySeek = () => {
            player.currentTime(seekSeconds);
          };
          // loadedmetadata may have already fired before this ready() callback runs.
          if (player.readyState() >= 1) {
            applySeek();
          } else {
            player.one("loadedmetadata", applySeek);
          }
        }
      } catch {
        // Defensive — never let deep-link seek break player init.
      }

      // Resume across an orientation-change remount (playbackResume). Applied
      // after the ?t= seek so the viewer's real progress wins over a deep-link
      // on rotate. Same-experience only; no-op on first load / chain advance.
      try {
        const resume = playbackResume.consume(
          self.props.experienceState.experienceData.idexperience,
        );
        // Resume when we saved a real position OR the video was playing. A fast
        // rotate while the source is still reloading can leave currentTime at ~0,
        // but a playing video must keep playing after the remount (else it
        // silently stops after a couple of quick rotations).
        if (resume && (resume.time > 0 || resume.playing)) {
          const player = self.player;
          // Restore the viewer's mute state across the rotation remount — the
          // fresh player defaults to unmuted, which silently un-mutes a muted
          // video on every rotate.
          try {
            player.muted(resume.muted);
          } catch {
            /* ignore */
          }
          const applyResume = () => {
            if (resume.time > 0) {
              player.currentTime(resume.time);
            }
            if (resume.playing) {
              // An unmuted programmatic play() on the freshly-swapped element is
              // rejected by the autoplay policy (a device rotation is not a user
              // gesture), and after the gesture-activation window lapses that
              // rejection is what makes playback stop after a few rotations. Fall
              // back to muted so playback continues — same recovery the chain
              // advance's `tryPlay` uses (continuity beats sound-vs-silent).
              (player.play() as Promise<void>).catch(() => {
                try {
                  player.muted(true);
                } catch {
                  /* ignore */
                }
                (player.play() as Promise<void>).catch(() => {
                  /* ignore the rejection */
                });
              });
            }
          };
          if (player.readyState() >= 1) {
            applyResume();
          } else {
            player.one("loadedmetadata", applyResume);
          }
        }
      } catch {
        /* best-effort resume */
      }
    });

    source.updatePlayer();
    // Same iOS recomposite for a first load that mounts already in landscape
    // (no rotate/adopt) — no-op off iOS / off landscape.
    recomposite.recompositeLandscapeControls();
  }

  function update() {
    // Keep the persistent player's pixel dimensions in lock-step with the
    // current orientation's template on EVERY re-render — not just at adopt. On
    // iOS the first resize after a rotate can report stale (landscape) viewport
    // dims, so the adopt-time measurement is sometimes wrong; a corrective resize
    // fires moments later and re-renders us. A fresh player re-measured itself;
    // the persistent player must re-sync here or it keeps the stale size,
    // leaving the right-side gap in portrait after a couple of rotates.
    source.syncPlayerDimensions();
    source.updatePlayer();
    // Logo URL may only become available after the experience data loads (a beat
    // after the player mounts), and it changes on a chain advance — refresh it.
    events.ensureLogoOverlay();
  }

  function unmount() {
    // Stop any in-flight control-bar recomposite so its rAF loop can't touch the
    // parked player after this mount is gone (a rotate remounts us, and the
    // fresh mount starts its own).
    if (self.cancelCtrlRecomposite) {
      self.cancelCtrlRecomposite();
    }

    // Best-effort resume net for any remount that ISN'T an adopt (harmless when
    // the next mount adopts, which never consumes it). Read state BEFORE
    // detaching.
    if (self.player && !self.player.isDisposed()) {
      try {
        const expData = self.props.experienceState.experienceData;
        if (expData && !self.player.error()) {
          playbackResume.save(
            expData.idexperience,
            self.player.currentTime() as number,
            !self.player.paused(),
            self.player.muted(),
          );
        }
      } catch {
        /* best-effort */
      }
    }

    // Detach this mount's listeners + decorations WITHOUT disposing the player:
    // a rotate remounts <VideoJs> for the same experience and the fresh mount
    // re-adopts the live player.
    events.detachFromPlayer();

    // Park the live `.video-js` node off-screen (but IN the document) BEFORE
    // React tears this layout down, so the media element is never detached
    // (which pauses playback on iOS). The player is NOT disposed here — the
    // registry owns its lifetime: a rotate re-adopts it, and Experience's
    // cleanup disposes it on a real teardown / chain advance (which flips the
    // store to LOADING, unmounting Experience).
    if (self.player && !self.player.isDisposed()) {
      playerRegistry.park(self.player);
    }
    self.player = null;
  }

  return { mount, update, unmount };
}

export type PlayerLifecycle = ReturnType<typeof createPlayerLifecycle>;

/** mount / update / unmount of one VideoJs mount; stable for the mount. */
export function usePlayerCreation(
  self: VideoJsSelf,
  tracking: VideoTracking,
  fullscreen: VideoFullscreen,
  source: PlayerSource,
  events: PlayerEvents,
  recomposite: LandscapeRecomposite,
): PlayerLifecycle {
  const [lifecycle] = useState(() =>
    createPlayerLifecycle(
      self,
      tracking,
      fullscreen,
      source,
      events,
      recomposite,
    ),
  );
  return lifecycle;
}
